import { formatExpectedShipDateRange } from "@faire/web--source/common/expectedShipDateRange";
import { intlDate } from "@faire/web--source/common/localization/date-fns/intlDate";
import { MediumFormatting } from "@faire/web--source/common/localization/formatting";
import { Button } from "@faire/web--source/slate/Button";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import supportArticle from "@faire/web-api--source/routes/support/articles/articleId";
import React from "react";
import styled from "styled-components";

import { ProductAddToCartState } from "@faire/retailer/views/product/ProductDetails/AddToCart/useProductAddToCartState";

interface IProps
  extends Pick<
    ProductAddToCartState,
    | "isOpenSizingVariant"
    | "openSizingShipDateRange"
    | "baseOrderByDate"
    | "openSizingAllSameDates"
    | "selectedOption"
  > {
  product: IProductPageResponse.IPdpProduct;
  isPreorderExcludedFromPromo?: boolean;
}

const FAIRE_PREORDER_SUPPORT_ARTICLE = supportArticle("360038557671");

export const PreorderMessage: React.FC<IProps> = ({
  isOpenSizingVariant,
  openSizingShipDateRange,
  baseOrderByDate,
  openSizingAllSameDates,
  selectedOption,
  product,
  isPreorderExcludedFromPromo,
}) => {
  let expectedShipDateRangeString: string | undefined = undefined;

  if (isOpenSizingVariant && openSizingShipDateRange) {
    expectedShipDateRangeString = formatExpectedShipDateRange(
      {
        expected_ship_date:
          openSizingShipDateRange.startDate.normalizedUtcTimestamp,
        expected_ship_window_days: openSizingShipDateRange.windowDays,
      },
      "MMM d"
    );
  } else if (selectedOption) {
    expectedShipDateRangeString = formatExpectedShipDateRange(
      selectedOption,
      "MMM d"
    );
  }

  if (!expectedShipDateRangeString && product.shipping_information) {
    // default to use product level ship date
    expectedShipDateRangeString = formatExpectedShipDateRange(
      product.shipping_information,
      "MMM d"
    );
  }

  if (!expectedShipDateRangeString) {
    // if after pulling the date range from the product doesn't work, show nothing.
    return null;
  }

  // we show orderBy/stopSellingAt date when not open sizing
  // OR when open sizing and all options have same orderBy date
  const showPreorderWithOrderByDate =
    baseOrderByDate &&
    (!isOpenSizingVariant || (isOpenSizingVariant && openSizingAllSameDates));

  return (
    <PreorderMessageBox data-test-id="add-to-cart-preorder-message-box">
      <Typography>
        <>
          {showPreorderWithOrderByDate ? (
            <StrictLocalMsg
              defaultMessage="Preorder ships <MediumFormatting>{expectedShipDate}</MediumFormatting>. You’ll be charged when this preorder ships. All orders must be placed by {orderByDate}."
              description={{
                text: "Description about the preorder's shipment date. Includes the shipment date range, when the user will be charged.",
              }}
              values={{
                expectedShipDate: expectedShipDateRangeString,
                orderByDate:
                  intlDate(baseOrderByDate.normalizedUtcTimestamp, "MMM d") ??
                  "",
                MediumFormatting: MediumFormatting,
              }}
            />
          ) : (
            <StrictLocalMsg
              defaultMessage="Preorder ships <MediumFormatting>{expectedShipDate}</MediumFormatting>. You’ll be charged when this preorder ships."
              description={{
                text: "Description about the preorder's shipment date. Includes the shipment date range, when the user will be charged",
              }}
              values={{
                expectedShipDate: expectedShipDateRangeString,
                MediumFormatting: MediumFormatting,
              }}
            />
          )}{" "}
          {isPreorderExcludedFromPromo ? (
            <>
              <StrictLocalMsg
                defaultMessage="This brand has chosen to exclude preorder items from their market special."
                description={{
                  text: "Disclaimer that the Fair Market promotion does not apply to this item due to brand restriction",
                }}
              />{" "}
            </>
          ) : null}
          <LearnMoreLink />
        </>
      </Typography>
    </PreorderMessageBox>
  );
};

const LearnMoreLink: React.FC = () => {
  return (
    <Button
      variant="tertiary"
      target="_blank"
      linkTo={FAIRE_PREORDER_SUPPORT_ARTICLE}
    >
      <StrictLocalMsg
        defaultMessage="Learn more"
        description={{
          text: "Link to support article",
        }}
      />
    </Button>
  );
};

const PreorderMessageBox = styled.div`
  margin-top: 16px;
  text-align: left;
  width: 100%;
`;
